export const WAITLIST_CONSENT_VERSION = "waitlist-v1";

export interface WaitlistFields {
  email: string;
  phone: string;
  consent: boolean;
  website: string;
}

export interface WaitlistPayload {
  email: string;
  phone: string;
  consent: true;
  consent_version: typeof WAITLIST_CONSENT_VERSION;
  website: string;
}

export type WaitlistFieldErrors = Partial<Record<"email" | "phone" | "consent", string>>;

export function validateWaitlist(fields: WaitlistFields): {
  errors: WaitlistFieldErrors;
  payload: WaitlistPayload | null;
} {
  const email = fields.email.trim();
  // Formatting is allowed, but a caller must supply + and their own country code.
  const phone = fields.phone.trim().replace(/[\s().-]/g, "");
  const errors: WaitlistFieldErrors = {};

  if (!email) errors.email = "onboarding.error.emailRequired";
  else if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "onboarding.error.email";
  }
  if (!fields.phone.trim()) errors.phone = "onboarding.error.phoneRequired";
  else if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
    errors.phone = "onboarding.error.phone";
  }
  if (fields.consent !== true) errors.consent = "onboarding.error.consent";

  return {
    errors,
    payload: Object.keys(errors).length ? null : {
      email,
      phone,
      consent: true,
      consent_version: WAITLIST_CONSENT_VERSION,
      website: fields.website,
    },
  };
}

type WaitlistFailure = "invalid" | "rate-limited" | "unavailable" | "unconfirmed";

export class WaitlistError extends Error {
  constructor(public readonly reason: WaitlistFailure, public readonly retrySeconds: number | null = null) {
    // Never carry server detail or contact information into UI errors or logs.
    super(reason);
    this.name = "WaitlistError";
  }
}

export function parseRetryAfter(value: string | null, now = Date.now()): number | null {
  if (!value?.trim()) return null;
  const raw = value.trim();
  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    return Number.isSafeInteger(seconds) ? seconds : null;
  }
  const date = Date.parse(raw);
  return Number.isFinite(date) ? Math.max(0, Math.ceil((date - now) / 1000)) : null;
}

export async function submitWaitlist(payload: WaitlistPayload, signal: AbortSignal): Promise<void> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal.aborted) abort();
  signal.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(abort, 20_000);

  try {
    const response = await fetch("/api/v1/public/waitlist", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (response.status === 422) throw new WaitlistError("invalid");
    if (response.status === 429) {
      throw new WaitlistError("rate-limited", parseRetryAfter(response.headers.get("Retry-After")));
    }
    if (response.status === 404 || response.status === 503) throw new WaitlistError("unavailable");
    if (response.status !== 202) throw new WaitlistError("unconfirmed");

    const body: unknown = await response.json();
    if (!body || typeof body !== "object" || !("status" in body) || body.status !== "accepted") {
      throw new WaitlistError("unconfirmed");
    }
  } catch (error) {
    if (error instanceof WaitlistError) throw error;
    throw new WaitlistError("unconfirmed");
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
  }
}
